import { useEffect, useRef, useState } from 'react'
import type { ListParams, ListResult } from '../api'
import { LIST_SEARCH_DEBOUNCE_MS, DEFAULT_LIST_PAGE_SIZE } from '../../shared/constants'
import { HOOKS_USE_SERVER_LIST_TEXTS as TX } from '../../shared/texts'

// Shared state machine for server-side list views (search + sort + pagination).
// Pass a fetcher and a refetch `version` (bump from useDataVersion to reload when
// a related record changes elsewhere). Search is debounced; changing the query or
// sort resets to page 1. Reusable across Customers/Invoices/Subscriptions lists.

export type SortDir = 'asc' | 'desc'

export interface ServerList<T> {
    rows: T[]
    total: number
    grandTotal: number
    // Extra fields a list endpoint may return alongside rows/total (e.g. the transaction
    // list's sums over the FILTERED set). Passed through untouched.
    extra: Record<string, any>
    loading: boolean
    error: string
    q: string
    setQ: (q: string) => void
    sort: string
    dir: SortDir
    toggleSort: (field: string) => void
    page: number
    pageSize: number
    setPage: (page: number) => void
    setPageSize: (n: number) => void
    pageCount: number
}

export interface ServerListOptions {
    // Preselected search term (e.g. the Logs tab opened for one transaction id).
    initialQ?: string
    sort?: string
    dir?: SortDir
    pageSize?: number
    debounceMs?: number
    // Extra list-specific filter params (status/brand/…). Changing them refetches
    // and resets to page 1, like changing the search query.
    params?: Record<string, string | undefined>
}

export function useServerList<T>(
    fetcher: (params: ListParams) => Promise<ListResult<T>>,
    version = 0,
    opts: ServerListOptions = {},
): ServerList<T> {
    const [q, setQRaw] = useState(opts.initialQ ?? '')
    const [debouncedQ, setDebouncedQ] = useState(opts.initialQ ?? '')
    const [sort, setSort] = useState(opts.sort ?? 'createdAt')
    const [dir, setDir] = useState<SortDir>(opts.dir ?? 'desc')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSizeState] = useState(opts.pageSize ?? DEFAULT_LIST_PAGE_SIZE)
    const setPageSize = (n: number) => { setPageSizeState(n); setPage(1) }

    const [rows, setRows] = useState<T[]>([])
    const [total, setTotal] = useState(0)
    const [grandTotal, setGrandTotal] = useState(0)
    const [extra, setExtra] = useState<Record<string, any>>({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    // Debounce the free-text query; a new search starts back on page 1.
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedQ(q); setPage(1) }, opts.debounceMs ?? LIST_SEARCH_DEBOUNCE_MS)
        return () => clearTimeout(t)
    }, [q, opts.debounceMs])

    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    // Stable signature of the extra filter params; a change resets to page 1.
    const paramsKey = JSON.stringify(opts.params ?? {})
    const firstParams = useRef(true)
    useEffect(() => {
        if (firstParams.current) { firstParams.current = false; return }
        setPage(1)
    }, [paramsKey])

    useEffect(() => {
        let alive = true
        setLoading(true)
        fetcherRef.current({ q: debouncedQ, sort, dir, page, pageSize, ...(opts.params ?? {}) })
            .then((res) => {
                if (!alive) return
                const { rows: r, total: t, grandTotal: g, ...rest } = res as any
                setRows(r); setTotal(t); setGrandTotal(g); setExtra(rest); setError('')
            })
            .catch((e: any) => { if (alive) setError(e?.response?.data?.error ?? e?.message ?? TX.loadError) })
            .finally(() => { if (alive) setLoading(false) })
        return () => { alive = false }
    }, [debouncedQ, sort, dir, page, pageSize, version, paramsKey])

    function toggleSort(field: string) {
        if (field === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
        else { setSort(field); setDir('asc') }
        setPage(1)
    }

    function setQ(next: string) { setQRaw(next) }

    const pageCount = Math.max(1, Math.ceil(total / pageSize))
    return { rows, total, grandTotal, extra, loading, error, q, setQ, sort, dir, toggleSort, page, pageSize, setPage, setPageSize, pageCount }
}
